import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DocumentTextIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Dropdown from '../components/ui/Dropdown';
import { useAuth } from '../contexts/AuthContext';
import LeaveService from '../services/leave.service';
import toast from '../utils/toast';

const DEFAULT_FORM = {
  name: '',
  code: '',
  isPaid: true,
  annualLimit: 21,
  monthlyLimit: 2,
  carryForward: false,
  description: '',
  color: '#3B82F6'
};

const LeaveManagement = () => {
  const { isAdmin, isHR } = useAuth();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [searchTerm, setSearchTerm] = useState('');
  const [paidFilter, setPaidFilter] = useState('');

  useEffect(() => {
    if (isAdmin || isHR) {
      fetchLeaveTypes();
    }
  }, [isAdmin, isHR]);

  const fetchLeaveTypes = async () => {
    try {
      setLoading(true);
      const response = await LeaveService.getLeaveTypes();
      setLeaveTypes(response || []);
    } catch (error) {
      console.error('Error fetching leave types:', error);
      if (error.response?.status !== 404) {
        const msg = error.response?.data?.message || 'Failed to load leave types';
        toast.error(msg);
      }
      setLeaveTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(DEFAULT_FORM);
    setEditing(null);
  };

  const openAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (lt) => {
    setEditing(lt);
    setForm({
      name: lt.name || '',
      code: lt.code || '',
      isPaid: !!lt.isPaid,
      annualLimit: Number(lt.annualLimit ?? 0),
      monthlyLimit: Number(lt.monthlyLimit ?? 0),
      carryForward: !!lt.carryForward,
      description: lt.description || '',
      color: lt.color || '#3B82F6'
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const validate = () => {
    if (!form.name.trim()) {
      toast.error('Please enter a leave name');
      return false;
    }
    if (!form.code.trim()) {
      toast.error('Please enter a leave code');
      return false;
    }
    if (Number.isNaN(Number(form.annualLimit)) || Number(form.annualLimit) < 0) {
      toast.error('Annual limit must be 0 or greater');
      return false;
    }
    if (Number.isNaN(Number(form.monthlyLimit)) || Number(form.monthlyLimit) < 0) {
      toast.error('Monthly limit must be 0 or greater');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        annualLimit: Number(form.annualLimit),
        monthlyLimit: Number(form.monthlyLimit),
        days: Number(form.annualLimit)
      };
      if (editing) {
        await LeaveService.updateLeaveType(editing.id, payload);
        toast.success('Leave type updated successfully');
      } else {
        await LeaveService.createLeaveType(payload);
        toast.success('Leave type created successfully');
      }
      closeForm();
      fetchLeaveTypes();
    } catch (error) {
      console.error('Error saving leave type:', error);
      const msg = error.message || error.response?.data?.message || 'Failed to save leave type';
      toast.error(msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this leave type?')) return;
    try {
      await LeaveService.deleteLeaveType(id);
      toast.success('Leave type deleted successfully');
      fetchLeaveTypes();
    } catch (error) {
      console.error('Error deleting leave type:', error);
      const msg = error.message || error.response?.data?.message || 'Failed to delete leave type';
      toast.error(msg);
    }
  };

  if (!isAdmin && !isHR) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Card>
          <Card.Body className="p-12 text-center">
            <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-600">Only Admins and HRs can manage leave types.</p>
          </Card.Body>
        </Card>
      </div>
    );
  }

  const filtered = leaveTypes.filter((lt) => {
    const q = searchTerm.trim().toLowerCase();
    const matchesSearch = !q
      ? true
      : (lt.name || '').toLowerCase().includes(q) ||
        (lt.code || '').toLowerCase().includes(q) ||
        (lt.description || '').toLowerCase().includes(q);
    const matchesPaid =
      paidFilter === ''
        ? true
        : paidFilter === 'paid'
        ? !!lt.isPaid
        : !lt.isPaid;
    return matchesSearch && matchesPaid;
  });

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 rounded-xl p-6 text-white relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Leave Management</h1>
            <p className="text-purple-100">Configure leave types, limits and carry-forward rules</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{leaveTypes.length}</div>
            <div className="text-sm text-purple-200">Leave Types</div>
          </div>
        </div>
      </motion.div>

      <Card>
        <Card.Body className="p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-1 flex-col gap-4 sm:flex-row sm:items-center">
              <div className="relative flex-1 min-w-0 sm:max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search leave types..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                />
              </div>
              <Dropdown
                value={paidFilter}
                onChange={setPaidFilter}
                options={[
                  { value: '', label: 'All' },
                  { value: 'paid', label: 'Paid only' },
                  { value: 'unpaid', label: 'Unpaid only' }
                ]}
                placeholder="All"
                className="w-full sm:w-[180px]"
              />
            </div>
            <Button onClick={openAdd} icon={PlusIcon}>
              Add Leave Type
            </Button>
          </div>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-56 bg-gray-200 animate-pulse rounded-xl"></div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <Card.Body className="p-12 text-center">
            <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No leave types found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || paidFilter
                ? 'Try adjusting your filters.'
                : 'Add your first leave type to get started.'}
            </p>
            <Button onClick={openAdd} icon={PlusIcon}>
              Add Leave Type
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((lt, index) => (
            <motion.div
              key={lt.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(index * 0.05, 0.3) }}
            >
              <Card className="hover:shadow-lg transition-shadow duration-300 h-full">
                <Card.Body className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: lt.color || '#3B82F6' }}
                      >
                        {lt.code}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{lt.name}</h3>
                        {lt.description && (
                          <p className="text-sm text-gray-600 line-clamp-1">
                            {lt.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => openEdit(lt)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(lt.id)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Row
                      label="Type"
                      value={
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            lt.isPaid
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {lt.isPaid ? 'Paid' : 'Unpaid'}
                        </span>
                      }
                    />
                    <Row label="Annual Limit" value={`${lt.annualLimit ?? 0} days`} />
                    <Row label="Monthly Limit" value={`${lt.monthlyLimit ?? 0} days`} />
                    <Row
                      label="Carry Forward"
                      value={
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            lt.carryForward
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {lt.carryForward ? 'Yes' : 'No'}
                        </span>
                      }
                    />
                  </div>
                </Card.Body>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={closeForm}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', damping: 26, stiffness: 320 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900">
                  {editing ? 'Edit Leave Type' : 'Add New Leave Type'}
                </h2>
                <button
                  onClick={closeForm}
                  className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <form
                onSubmit={handleSubmit}
                className="p-6 space-y-4 overflow-y-auto"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Leave Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, name: e.target.value }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      placeholder="e.g., Annual Leave"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Leave Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.code}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          code: e.target.value.toUpperCase()
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      placeholder="e.g., AL"
                      maxLength="3"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, description: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    rows="2"
                    placeholder="Brief description..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Annual Limit (Days) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={form.annualLimit}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, annualLimit: e.target.value }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      min="0"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Monthly Limit (Days) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={form.monthlyLimit}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, monthlyLimit: e.target.value }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      min="0"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, color: e.target.value }))
                      }
                      className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={form.color}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, color: e.target.value }))
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      placeholder="#3B82F6"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isPaid}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, isPaid: e.target.checked }))
                      }
                      className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500"
                    />
                    <span className="text-sm text-gray-700">Paid leave type</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.carryForward}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, carryForward: e.target.checked }))
                      }
                      className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500"
                    />
                    <span className="text-sm text-gray-700">
                      Allow carry forward to next year
                    </span>
                  </label>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="submit" className="flex-1">
                    {editing ? 'Update Leave Type' : 'Create Leave Type'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeForm}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-center">
    <span className="text-sm text-gray-600">{label}</span>
    <span className="font-medium text-sm">{value}</span>
  </div>
);

export default LeaveManagement;
